import { motion } from "framer-motion";
import { SlidersHorizontal, Mic, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { VoicePreferencesSection } from "@/components/voice-preferences-section";
import { useGetVoiceProfile } from "@workspace/api-client-react";
import { useAppStore } from "@/store";

export default function VoiceStyle() {
  const { sessionId } = useAppStore();
  const { data: profile } = useGetVoiceProfile({ request: { headers: { "x-session-id": sessionId } } });

  const sampleCount = profile?.sampleCount ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Voice Style</h1>
            <p className="text-sm text-muted-foreground">
              Fine-tune how the AI expresses your unique voice across every post.
            </p>
          </div>
        </div>
      </motion.div>

      {sampleCount === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5 flex items-start gap-4"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
            <Mic className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground mb-1">
              Train your voice first for best results
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              These settings work alongside your trained voice samples. Add a few writing samples so the AI can learn your natural style — then use this page to dial in the finer details.
            </p>
            <Link href="/training">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors">
                Go to Voice Training <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>
        </motion.div>
      )}

      {sampleCount > 0 && sampleCount < 3 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-primary/20 bg-primary/5 p-5 flex items-start gap-4"
        >
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Mic className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground mb-1">
              Voice partially trained — {sampleCount} sample{sampleCount === 1 ? "" : "s"} so far
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              Your style settings below will start applying immediately. Add 3+ samples to your Voice Training for the most accurate results.
            </p>
            <Link href="/training">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                Add more samples <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>
        </motion.div>
      )}

      {sampleCount >= 3 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-4 flex items-center gap-3"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
          <p className="text-xs text-emerald-300/90">
            Voice DNA active — {sampleCount} samples trained. These preferences layer on top and override the AI's defaults.
          </p>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        <VoicePreferencesSection />
      </motion.div>
    </div>
  );
}
